/**
 * Single source of truth for changelog entries and "latest" version.
 * Latest is a numeric timestamp (ms); when we add entries, newest entry has the highest timestamp.
 */

export interface ChangelogEntry {
  date: string;
  timestamp: number;
  version?: string;
  type: 'feature' | 'improvement' | 'fix' | 'update';
  title: string;
  description?: string;
}

export const CHANGELOG_STORAGE_KEY = 'changelogLastSeenVersion';
export const CHANGELOG_LOCAL_STORAGE_KEY = 'changelog_last_seen';

export const changelogEntries: ChangelogEntry[] = [
  {
    date: '2026-10-22',
    timestamp: 1792670400000,
    type: 'feature',
    title: 'Restrict access to your networks',
    description:
      'Only people on networks you choose, like your office, can open your guides and help centers. Add single addresses or ranges, IPv4 or IPv6, and label them so you know which is which. Set it for guides shared by link (including the widget) under Shared Guides, or per help center on its Security tab. If a guide or help center also has a password, people on your networks need to enter it too. Available on paid plans.',
  },
  {
    date: '2026-10-22',
    timestamp: 1792670340000,
    type: 'improvement',
    title: 'Guides Branding is now Shared Guides',
    description:
      'The settings for guides shared by link moved to a page called Shared Guides, with Appearance and Access sections. Old links to the page still work.',
  },
  {
    date: '2026-09-22',
    timestamp: 1790078400000,
    type: 'feature',
    title: 'Tables in articles',
    description:
      'Articles can now have tables. Type /table to get one with a header row, then add rows and columns, merge cells, or drag a column edge to resize it. Wide tables scroll on phones instead of breaking the page.',
  },
  {
    date: '2026-09-22',
    timestamp: 1790078340000,
    type: 'feature',
    title: 'Callouts',
    description:
      'Put a note in a tinted box with an icon so nobody scrolls past it. There are four kinds: info, tip, warning and danger. Type /info, /tip, /warning or /danger, or pick a type from the box after you add one.',
  },
  {
    date: '2026-07-31',
    timestamp: 1785499320000,
    type: 'feature',
    title: 'Per-article SEO controls',
    description:
      'Fine-tune how each article appears in search and when shared. In the new "SEO & social" section of the article editor you can set a custom SEO title and meta description, or hide an individual article from search engines. Available on paid plans; free articles use their title and description automatically. Every help center also now gets Open Graph tags, structured data, and a sitemap.xml out of the box.',
  },
  {
    date: '2026-07-31',
    timestamp: 1785499260000,
    type: 'feature',
    title: 'FAQ blocks',
    description:
      'Add a list of questions and answers to any article. Insert an FAQ block from the editor toolbar or type /faq. Readers get a clean, expandable accordion, and search engines get proper FAQ structured data so your answers can appear as rich results.',
  },
  {
    date: '2026-07-31',
    timestamp: 1785499200000,
    type: 'improvement',
    title: 'Automatic redirects when a URL changes',
    description:
      'Rename an article and its old link keeps working. When an article\'s URL changes, anyone who follows the old link is sent to the new one automatically, so bookmarks and search results do not break.',
  },
  {
    date: '2026-07-25',
    timestamp: 1784980920000,
    type: 'feature',
    title: 'Draft & Publish for articles',
    description:
      'Editing no longer goes live instantly. New articles start as a draft and stay hidden until you Publish, and changes to existing articles are saved privately until you publish them — so readers always see the finished version. Use the new "Visible" toggle to show or hide a page in the help center.',
  },
  {
    date: '2026-07-25',
    timestamp: 1784980900000,
    type: 'feature',
    title: 'Preview drafts before publishing',
    description:
      'See exactly how an unpublished draft will look on your live help center. Click Preview in the article editor to open a private, unlisted preview of your draft — no need to publish first.',
  },
  {
    date: '2026-07-25',
    timestamp: 1784980800000,
    type: 'feature',
    title: 'Version history',
    description:
      'Every time you edit and save an article, we now keep a version automatically. Open "Version history" in the article editor to browse previous versions and restore any of them with one click. Free plans keep the last 2 versions; paid plans keep the last 10.',
  },
  {
    date: '2026-07-25',
    timestamp: 1784980740000,
    type: 'feature',
    title: 'Compare versions',
    description:
      'See exactly what changed. In an article\'s version history, pick two versions to compare and view the differences highlighted inline — added text in green, removed in red — including changes to the title.',
  },
  {
    date: '2026-07-25',
    timestamp: 1784980680000,
    type: 'feature',
    title: 'Distraction-free writing mode',
    description:
      'Focus on your words. Use the expand icon in the editor toolbar to open a full-screen writing mode with everything else tucked away, and press Esc to exit.',
  },
  {
    date: '2026-07-25',
    timestamp: 1784980620000,
    type: 'improvement',
    title: 'A cleaner article editor',
    description:
      'The article editor got a refresh: a calmer, more focused layout, an always-visible save status so you know when your work is saved, one-click preview to open the live page, and quick access to the article\'s public URL.',
  },
  {
    date: '2026-06-05',
    timestamp: 1780660800000,
    type: 'feature',
    title: 'Homepage highlights',
    description:
      'You can now hand-pick what shows up first on your help center homepage. Pin a category or article from the structure view, arrange them how you like, and visitors will see them up front in a Highlights section.',
  },
  {
    date: '2026-06-05',
    timestamp: 1780657200000,
    type: 'feature',
    title: 'Table of contents in the sidebar',
    description:
      'Prefer your page outline next to the navigation? You can show the table of contents in the left sidebar so readers can jump between sections without looking to the right. Turn it on in Help Center settings.',
  },
  {
    date: '2026-04-07',
    timestamp: 1744041600000,
    type: 'feature',
    title: 'License Manager localized in 5 languages',
    description: 'The License Manager is now available in English, German, French, Spanish, and Japanese. Use the language switcher in the header to change the interface language.',
  },
  {
    date: '2026-02-05',
    version: '1.0.4',
    timestamp: 1738749600000,
    type: 'feature',
    title: 'Custom domains for Help Centers',
    description: 'Use your own domain for your help center (e.g. help.yourcompany.com). Available on paid plans: go to Help Center settings → Domain, add your domain, then add the CNAME or A record at your DNS provider. Need help? Contact us at hello@folge.me.',
  },
  {
    date: '2026-02-04',
    timestamp: 1738663200000,
    type: 'feature',
    title: 'Custom robots.txt & SEO settings',
    description: 'Control how search engines crawl your help center. In Help Center settings → SEO you can turn search indexing on or off and, on paid plans, define a custom robots.txt (e.g. to block specific paths). Crawlers see your robots.txt at your help center URL + /robots.txt.',
  },
  {
    date: '2026-01-22',
    version: '1.0.3',
    timestamp: 1737547200000,
    type: 'feature',
    title: 'Help Center Search',
    description: 'Full-text search for Help Centers. Press ⌘K (Mac) or Ctrl+K (Windows) to instantly search across articles, guides, and categories. Results are ranked by relevance with highlighted matches.',
  },
  {
    date: '2026-01-22',
    timestamp: 1737543600000,
    type: 'improvement',
    title: 'Faster Page Loads',
    description: 'Help Center pages now load faster with optimized API calls.',
  },
  {
    date: '2026-01-16',
    version: '1.0.2',
    timestamp: 1737043200000,
    type: 'feature',
    title: 'Image Editor',
    description: 'Edit and transform article images with a built-in editor. Crop, rotate, add shapes, arrows, text annotations, and more—all without leaving the editor. Perfect your visuals before publishing.',
  },
  {
    date: '2026-01-16',
    timestamp: 1737039600000,
    type: 'feature',
    title: 'Document Import',
    description: 'Import documents directly as articles into your editor. Simply upload your Word documents (.docx), PDFs, Markdown files, or plain text files and prefill the article with the content of the document.',
  },
  {
    date: '2026-01-12',
    version: '1.0.0',
    timestamp: 1736611200000,
    type: 'improvement',
    title: 'Help Center Launched',
    description: 'Help Center is now available to all users! Create beautiful, searchable knowledge bases for your team and customers. Share guides, articles, and documentation with ease.',
  },
];

export const LATEST_CHANGELOG_VERSION =
  changelogEntries[0]?.timestamp ?? 0;
